import { getAccessToken } from "../../utils/auth";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import * as XLSX from "xlsx";
import axios from "../../utils/api.js";

const HospitalDetails = () => {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHospitals = async () => {
      const token = getAccessToken();
      try {
        const response = await axios.get("/get-hospitals", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setHospitals(response.data);
      } catch (error) {
        console.error("Error fetching hospitals:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHospitals();
  }, []);

  // Function to auto-size columns based on the longest data entry
  const autoFitColumns = (jsonData) => {
    return Object.keys(jsonData[0] || {}).map((key) => ({
      wch:
        Math.max(
          key.length, // Minimum width should fit the header text
          ...jsonData.map((row) => (row[key] ? row[key].toString().length : 0)) // Longest cell
        ) + 5, // Add some extra padding
    }));
  };

  // Function to download Excel
  const handleDownloadExcel = () => {
    if (hospitals.length === 0) {
      alert("No data available to download.");
      return;
    }
    const basicInfoSheet = hospitals.map((hospital, index) => ({
      "S. No": index + 1,
      "Hospital Name": hospital.basicInfo.hospitalName,
      Type: hospital.basicInfo.hospitalType,
      Size: hospital.basicInfo.hospitalSize,
      "Total Floor Area": hospital.basicInfo.totalFloorArea,
      "Total Floors": hospital.basicInfo.totalFloors,
      Departments: hospital.basicInfo.totalDepartments,
      "Total Beds": hospital.basicInfo.totalBeds,
      Accreditations: hospital.basicInfo.accreditationCertifications,
    }));

    const dataCollectionSheet = hospitals.map((hospital, index) => ({
      "S. No": index + 1,
      "Hospital Name": hospital.basicInfo.hospitalName,
      "Date of Data Collection": new Date(
        hospital.dataCollection.dateOfDataCollection
      ).toLocaleDateString("en-GB"),
      "Data Collected By": hospital.dataCollection.dataCollectedBy,
      "Data Source": hospital.dataCollection.dataSource,
    }));

    const infraPatientFlowSheet = hospitals.map((hospital, index) => ({
      "S.No": index + 1,
      "Hospital Name": hospital.basicInfo.hospitalName,
      "No Of Bedrooms/Wards": hospital.infraPatientFlow.noOfbedroomsOrWards,
      "Normal Room Cost/Day": hospital.infraPatientFlow.normalRoomCostPerDay,
      "Normal Room Occupancy Rate":
        hospital.infraPatientFlow.normalRoomOccupancyRate,
      "AC Room Cost/Day": hospital.infraPatientFlow.acRoomCostPerDay,
      "AC Room Occupancy Rate": hospital.infraPatientFlow.acRoomOccupancyRate,
      "ICU Ward Count": hospital.infraPatientFlow.icuWardCount,
      "ICU Cost/Day": hospital.infraPatientFlow.icuCostPerDay,
      "ICU Ward Occupancy Rate": hospital.infraPatientFlow.icuWardOccupancyRate,
      "Operation Theaters": hospital.infraPatientFlow.operationTheaters,
      "OT Occupancy Rate": hospital.infraPatientFlow.otOccupancyRate,
      "MRI/CT Scan Rooms": hospital.infraPatientFlow.mriCtScanRooms,
      "MRI/CT Scan Utilization Rate":
        hospital.infraPatientFlow.mriCtScanUtilizationRate,
      "Outpatient Consultation Rooms":
        hospital.infraPatientFlow.outpatientConsultationRooms,
      "Outpatient Area Occupancy/Day":
        hospital.infraPatientFlow.outpatientAreaOccupancyPerDay,
      "Reception Waiting Footfall/Day":
        hospital.infraPatientFlow.receptionWaitingFootfallPerDay,
    }));

    const hygieneSheet = hospitals.map((hospital, index) => ({
      "S.No": index + 1,
      "Hospital Name": hospital.basicInfo?.hospitalName || "N/A",
      "Total Cleaning Budget (Per Month)":
        hospital.hygieneDisinfection?.totalCleaningBudgetPerMonth || "N/A",
      "Cleaning Costs - Bedrooms/Wards":
        hospital.hygieneDisinfection?.cleaningCostsbedroomsWards || "N/A",
      "Cleaning Costs - ICU Units":
        hospital.hygieneDisinfection?.cleaningCostsicuUnits || "N/A",
      "Cleaning Costs - Operation Theaters":
        hospital.hygieneDisinfection?.cleaningCostsoperationTheaters || "N/A",
      "Cleaning Costs - MRI Scanning Units":
        hospital.hygieneDisinfection?.cleaningCostsmriScanningUnits || "N/A",
      "Cleaning Costs - Outpatient Areas":
        hospital.hygieneDisinfection?.cleaningCostsoutpatientAreas || "N/A",
      "Cleaning Costs - Pharmacy Areas":
        hospital.hygieneDisinfection?.cleaningCostspharmacyAreas || "N/A",
      "Cleaning Costs - Reception Areas":
        hospital.hygieneDisinfection?.cleaningCostsreceptionAreas || "N/A",
      "Cleaning Costs - Canteens/Kitchen":
        hospital.hygieneDisinfection?.cleaningCostscanteensKitchen || "N/A",
      "Current Disinfection Techniques":
        hospital.hygieneDisinfection?.currentDisinfectionTechniques?.join(
          ", "
        ) || "N/A",
      "Avg Time Per Disinfection":
        hospital.hygieneDisinfection?.avgTimePerDisinfection || "N/A",
      "Disinfection Staff Availability":
        hospital.hygieneDisinfection?.disinfectionStaffAvailability?.join(
          ", "
        ) || "N/A",
      "Challenges in Cleaning & Disinfection":
        hospital.hygieneDisinfection?.challengesinCurrentCleaningandDisinfection?.join(
          ", "
        ) || "N/A",
    }));

    const infectionControlSheet = hospitals.map((hospital, index) => ({
      "S.No": index + 1,
      "Hospital Name": hospital.basicInfo?.hospitalName || "N/A",
      "Has Infection Control Team": hospital.infectionControl
        ?.hasInfectionControlTeam
        ? "Yes"
        : "No",
      "Standard Protocols Followed":
        hospital.infectionControl?.standardProtocolsFollowed?.join(", ") ||
        "N/A",
      "Audit Frequency":
        hospital.infectionControl?.auditFrequency?.join(", ") || "N/A",
      "Past Nosocomial Incidents":
        hospital.infectionControl?.pastNosocomialIncidents || "N/A",
      "Recent Hygiene Complaints":
        hospital.infectionControl?.recentHygieneComplaints?.join(", ") || "N/A",
    }));

    const staffExposureSheet = hospitals.map((hospital, index) => ({
      "S.No": index + 1,
      "Hospital Name": hospital.basicInfo?.hospitalName || "N/A",
      "Avg Time Spent Cleaning":
        hospital.staffExposure?.avgTimeSpentCleaning || "N/A",
      "Disinfection Complaints":
        hospital.staffExposure?.disinfectionComplaints?.join(", ") || "N/A",
      "Reports of Chemical Exposure":
        hospital.staffExposure?.reportsChemicalExposure?.join(", ") || "N/A",
      "Current PPE Usage":
        hospital.staffExposure?.currentPpeUsage?.join(", ") || "N/A",
      "Automation Readiness": hospital.staffExposure?.automationReadiness
        ? "Yes"
        : "No",
    }));

    const timeCostSheet = hospitals.map((hospital, index) => ({
      "S.No": index + 1,
      "Hospital Name": hospital.basicInfo?.hospitalName || "N/A",
      "Avg Manual Disinfection Time (Per Day)":
        hospital.timeCostAnalysis?.avgManualDisinfectionTimePerDay || "N/A",
      "High-Risk Area Disinfection Time":
        hospital.timeCostAnalysis?.highRiskAreaDisinfectionTime || "N/A",
      "Chemical Disinfection Dry Time":
        hospital.timeCostAnalysis?.chemicalDisinfectionDryTime || "N/A",
      "Labor Cost (Per Month)":
        hospital.timeCostAnalysis?.laborCostPerMonth || "N/A",
      "Disinfection Chemical Cost (Per Month)":
        hospital.timeCostAnalysis?.disinfectionChemicalCostPerMonth || "N/A",
      "Electricity Consumption":
        hospital.timeCostAnalysis?.electricityConsumption || "N/A",
      "Infection-Related Complication Cost":
        hospital.timeCostAnalysis?.infectionRelatedComplicationCost || "N/A",
    }));

    const additionalNotesData = hospitals.map((hospital, index) => ({
      "S.No": index + 1,
      "Hospital Name": hospital.basicInfo.hospitalName || "N/A",
      "Additional Notes": hospital.additionalnotes?.note || "N/A",
    }));
    const wb = XLSX.utils.book_new();

    // Convert JSON to Worksheet
    const ws1 = XLSX.utils.json_to_sheet(basicInfoSheet);
    const ws2 = XLSX.utils.json_to_sheet(dataCollectionSheet);
    const ws3 = XLSX.utils.json_to_sheet(infraPatientFlowSheet);
    const ws4 = XLSX.utils.json_to_sheet(hygieneSheet);
    const ws5 = XLSX.utils.json_to_sheet(infectionControlSheet);
    const ws6 = XLSX.utils.json_to_sheet(staffExposureSheet);
    const ws7 = XLSX.utils.json_to_sheet(timeCostSheet);
    const ws8 = XLSX.utils.json_to_sheet(additionalNotesData);

    // Auto-size columns based on content
    ws1["!cols"] = autoFitColumns(basicInfoSheet);
    ws2["!cols"] = autoFitColumns(dataCollectionSheet);
    ws3["!cols"] = autoFitColumns(infraPatientFlowSheet);
    ws4["!cols"] = autoFitColumns(hygieneSheet);
    ws5["!cols"] = autoFitColumns(infectionControlSheet);
    ws6["!cols"] = autoFitColumns(staffExposureSheet);
    ws7["!cols"] = autoFitColumns(timeCostSheet);
    ws8["!cols"] = autoFitColumns(additionalNotesData);

    // Set row height for better readability
    ws1["!rows"] = [{ hpx: 25 }];
    ws2["!rows"] = [{ hpx: 25 }];
    ws3["!rows"] = [{ hpx: 25 }];
    ws4["!rows"] = [{ hpx: 25 }];
    ws5["!rows"] = [{ hpx: 25 }];
    ws6["!rows"] = [{ hpx: 25 }];
    ws7["!rows"] = [{ hpx: 25 }];
    ws8["!rows"] = [{ hpx: 25 }];

    // Append sheets to workbook
    XLSX.utils.book_append_sheet(wb, ws1, "Basic Info");
    XLSX.utils.book_append_sheet(wb, ws2, "Data Collection");
    XLSX.utils.book_append_sheet(wb, ws3, "InfraPatient Flow");
    XLSX.utils.book_append_sheet(wb, ws4, "Hygiene & Disinfection Info");
    XLSX.utils.book_append_sheet(wb, ws5, "Infection Control Info");
    XLSX.utils.book_append_sheet(wb, ws6, "Staff Exposure Info");
    XLSX.utils.book_append_sheet(wb, ws7, "Time Cost Analysis");
    XLSX.utils.book_append_sheet(wb, ws8, "Additional Notes");

    // Get the current date
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(currentDate.getDate()).padStart(2, "0");

    // Format the date as dd-mm-yyyy
    const formattedDate = `${day}-${month}-${year}`;
    // Create the filename with the current date
    const filename = `HospitalData${formattedDate}.xlsx`;

    // Write and download Excel file
    XLSX.writeFile(wb, filename);
  };

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  if (loading) {
    return (
      <div
        style={{
          ...pageStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>
          Loading...
        </span>
      </div>
    );
  }
  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Hospital Data
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View and download hospital data
        </p>
      </div>

      <button style={downloadButton} onClick={handleDownloadExcel}>
        📥 Download Excel
      </button>

      {hospitals.length === 0 ? (
        <div style={noDataStyle}>
          <h1>No Data Available</h1>
        </div>
      ) : (
        <>
          <h2 style={heading}>Basic Info</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hospital Name</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Size</th>
                  <th style={thStyle}>Total Floor Area</th>
                  <th style={thStyle}>Total Floors</th>
                  <th style={thStyle}>Departments</th>
                  <th style={thStyle}>Total Beds</th>
                  <th style={thStyle}>Accreditations</th>
                </tr>
              </thead>
              <tbody>
                {hospitals.map((hospital, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{hospital.basicInfo.hospitalName}</td>
                    <td style={tdStyle}>{hospital.basicInfo.hospitalType}</td>
                    <td style={tdStyle}>{hospital.basicInfo.hospitalSize}</td>
                    <td style={tdStyle}>
                      {hospital.basicInfo.totalFloorArea || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.basicInfo.totalFloors || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.basicInfo.totalDepartments || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.basicInfo.totalBeds || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.basicInfo.accreditationCertifications}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Data Collection</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hospital Name</th>
                  <th style={thStyle}>Date of Data Collection</th>
                  <th style={thStyle}>Data Collected By</th>
                  <th style={thStyle}>Data Source</th>
                </tr>
              </thead>
              <tbody>
                {hospitals.map((hospital, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{hospital.basicInfo.hospitalName}</td>
                    <td style={tdStyle}>
                      {new Date(
                        hospital.dataCollection.dateOfDataCollection
                      ).toLocaleDateString("en-GB")}
                    </td>
                    <td style={tdStyle}>
                      {hospital.dataCollection.dataCollectedBy}
                    </td>
                    <td style={tdStyle}>
                      {hospital.dataCollection.dataSource}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Infrastructure & Patient Flow</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hospital Name</th>
                  <th style={thStyle}>No Of Bedrooms/Wards</th>
                  <th style={thStyle}>Normal Room Cost/Day</th>
                  <th style={thStyle}>Normal Room Occupancy</th>
                  <th style={thStyle}>AC Room Cost/Day</th>
                  <th style={thStyle}>AC Room Occupancy</th>
                  <th style={thStyle}>ICU Ward Count</th>
                  <th style={thStyle}>ICU Cost/Day</th>
                  <th style={thStyle}>ICU Ward Occupancy</th>
                  <th style={thStyle}>Operation Theaters</th>
                  <th style={thStyle}>OT Occupancy</th>
                  <th style={thStyle}>MRI/CT Scan Rooms</th>
                  <th style={thStyle}>MRI/CT Utilization</th>
                  <th style={thStyle}>Outpatient Rooms</th>
                  <th style={thStyle}>Outpatient Area Occupancy</th>
                  <th style={thStyle}>Reception Footfall/Day</th>
                </tr>
              </thead>
              <tbody>
                {hospitals.map((hospital, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hospital.basicInfo.hospitalName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.noOfbedroomsOrWards || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.normalRoomCostPerDay || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.normalRoomOccupancyRate ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.acRoomCostPerDay || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.acRoomOccupancyRate || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.icuWardCount || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.icuCostPerDay || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.icuWardOccupancyRate || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.operationTheaters || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.otOccupancyRate || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.mriCtScanRooms || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.mriCtScanUtilizationRate ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow.outpatientConsultationRooms ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow
                        .outpatientAreaOccupancyPerDay || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infraPatientFlow
                        .receptionWaitingFootfallPerDay || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Hygiene & Disinfection Info</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hospital Name</th>
                  <th style={thStyle}>Total Cleaning Budget (Per Month)</th>
                  <th style={thStyle}>Cleaning Costs - Bedrooms/Wards</th>
                  <th style={thStyle}>Cleaning Costs - ICU Units</th>
                  <th style={thStyle}>Cleaning Costs - Operation Theaters</th>
                  <th style={thStyle}>Cleaning Costs - MRI Scanning Units</th>
                  <th style={thStyle}>Cleaning Costs - Outpatient Areas</th>
                  <th style={thStyle}>Cleaning Costs - Pharmacy Areas</th>
                  <th style={thStyle}>Cleaning Costs - Reception Areas</th>
                  <th style={thStyle}>Cleaning Costs - Canteens/Kitchen</th>
                  <th style={thStyle}>Current Disinfection Techniques</th>
                  <th style={thStyle}>Avg Time Per Disinfection</th>
                  <th style={thStyle}>Disinfection Staff Availability</th>
                  <th style={thStyle}>Challenges in Cleaning & Disinfection</th>
                </tr>
              </thead>
              <tbody>
                {hospitals.map((hospital, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{hospital.basicInfo.hospitalName}</td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection
                        .totalCleaningBudgetPerMonth || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection
                        .cleaningCostsbedroomsWards || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection.cleaningCostsicuUnits ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection
                        .cleaningCostsoperationTheaters || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection
                        .cleaningCostsmriScanningUnits || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection
                        .cleaningCostsoutpatientAreas || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection
                        .cleaningCostspharmacyAreas || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection
                        .cleaningCostsreceptionAreas || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection
                        .cleaningCostscanteensKitchen || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection.currentDisinfectionTechniques?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection.avgTimePerDisinfection ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection.disinfectionStaffAvailability?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.hygieneDisinfection.challengesinCurrentCleaningandDisinfection?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Infection Control Info</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hospital Name</th>
                  <th style={thStyle}>Has Infection Control Team</th>
                  <th style={thStyle}>Standard Protocols Followed</th>
                  <th style={thStyle}>Audit Frequency</th>
                  <th style={thStyle}>Past Nosocomial Incidents</th>
                  <th style={thStyle}>Recent Hygiene Complaints</th>
                </tr>
              </thead>
              <tbody>
                {hospitals.map((hospital, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{hospital.basicInfo.hospitalName}</td>
                    <td style={tdStyle}>
                      {hospital.infectionControl.hasInfectionControlTeam
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infectionControl.standardProtocolsFollowed?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infectionControl.auditFrequency?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infectionControl.pastNosocomialIncidents ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.infectionControl.recentHygieneComplaints?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Staff Exposure Info</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hospital Name</th>
                  <th style={thStyle}>Avg Time Spent Cleaning</th>
                  <th style={thStyle}>Disinfection Complaints</th>
                  <th style={thStyle}>Reports of Chemical Exposure</th>
                  <th style={thStyle}>Current PPE Usage</th>
                  <th style={thStyle}>Automation Readiness</th>
                </tr>
              </thead>
              <tbody>
                {hospitals.map((hospital, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{hospital.basicInfo.hospitalName}</td>
                    <td style={tdStyle}>
                      {hospital.staffExposure.avgTimeSpentCleaning || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.staffExposure.disinfectionComplaints?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.staffExposure.reportsChemicalExposure?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.staffExposure.currentPpeUsage?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.staffExposure.automationReadiness
                        ? "Yes"
                        : "No"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Time & Cost Analysis</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hospital Name</th>
                  <th style={thStyle}>
                    Avg Manual Disinfection Time (Per Day)
                  </th>
                  <th style={thStyle}>High-Risk Area Disinfection Time</th>
                  <th style={thStyle}>Chemical Disinfection Dry Time</th>
                  <th style={thStyle}>Labor Cost (Per Month)</th>
                  <th style={thStyle}>
                    Disinfection Chemical Cost (Per Month)
                  </th>
                  <th style={thStyle}>Electricity Consumption</th>
                  <th style={thStyle}>Infection-Related Complication Cost</th>
                </tr>
              </thead>
              <tbody>
                {hospitals.map((hospital, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{hospital.basicInfo.hospitalName}</td>
                    <td style={tdStyle}>
                      {hospital.timeCostAnalysis
                        .avgManualDisinfectionTimePerDay || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.timeCostAnalysis.highRiskAreaDisinfectionTime ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.timeCostAnalysis.chemicalDisinfectionDryTime ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.timeCostAnalysis.laborCostPerMonth || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.timeCostAnalysis
                        .disinfectionChemicalCostPerMonth || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.timeCostAnalysis.electricityConsumption ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.timeCostAnalysis
                        .infectionRelatedComplicationCost || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>* Additional Notes *</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Hospital Name</th>
                  <th style={thStyle}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {hospitals.map((hospital, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {hospital.basicInfo.hospitalName || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {hospital.additionalnotes?.note || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// Internal CSS styles
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "10px",
};

const thStyle = {
  backgroundColor: "#007bff",
  color: "#fff",
  padding: "10px",
  textAlign: "center",
  border: "1px solid #ddd",
};
const tableContainerStyle = {
  width: "100%",
  overflowX: "auto", // Enables horizontal scrolling
};
const tdStyle = {
  padding: "10px",
  border: "1px solid #ddd",
  textAlign: "center",
};

const heading = {
  fontSize: "24px",
  color: "black",
};

const downloadButton = {
  position: "absolute",
  top: "20px",
  right: "20px",
  backgroundColor: "#007bff",
  color: "white",
  border: "none",
  padding: "10px 15px",
  cursor: "pointer",
  borderRadius: "5px",
  fontSize: "14px",
};
const noDataStyle = {
  textAlign: "center",
  marginTop: "20px",
  fontSize: "20px",
  color: "#888",
};

export default HospitalDetails;
